import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Grid,
  Box,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate } from "react-router-dom";
import Pedido, {
  estadoInicialFiltroPesquisarPedido,
  FiltroPesquisarPedido,
} from "../Shared/Interfaces/Pedido";
import PrintIcon from "@mui/icons-material/Print";
import ShareIcon from "@mui/icons-material/Share";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { PedidoService } from "../../Services/PedidoService";
import React, { useContext, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import ImprimirPedido from "./ImprimirPedido";
import {
  formatDateToDDMMYYYY,
  formatToBRL,
} from "../../Utils/utils";
import { AppProvider } from "../../ContextApi/AppProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import DefaultLoader from "../Shared/DefaultLoader";
import colors from "../../theme/colors";

export default function ConsultarPedidos() {
  const navigate = useNavigate();
  const contextApi = useContext(AppProvider);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dataInicial, setDataInicial] = useState<Dayjs>(dayjs().subtract(3, 'day'));
  const [dataFinal, setDataFinal] = useState<Dayjs>(dayjs());
  const [filtroPesquisarPedido, setFiltroPesquisarPedido] =
    React.useState<FiltroPesquisarPedido>(estadoInicialFiltroPesquisarPedido);
  const [loading, setLoading] = useState(false);
  
  const handleInputPesquisarPedido = (
    fieldName: keyof FiltroPesquisarPedido,
    value: string | number
  ) => {
    if (fieldName == "idPedido" && value == "") {
      value = 0;
    }
    setFiltroPesquisarPedido((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pedidoService = new PedidoService();
  const [pedidos, setPedidos] = React.useState<Pedido[]>([]);
  
  const handlePrint = (pedido: Pedido) => {
    openPDFInNewTab(pedido);
  };

  const generatePedidoPdfFile = async (pedido: Pedido) => {
    const blob = await pdf(
      <ImprimirPedido data={pedido} numeroPedido={`#000${pedido.id}`} />
    ).toBlob();

    return new File([blob], `pedido-${pedido.id}.pdf`, {
      type: "application/pdf",
    });
  };

  const handleShare = async (pedido: Pedido) => {
    try {
      const file = await generatePedidoPdfFile(pedido);
      const shareData: ShareData = {
        files: [file],
        title: `Pedido #${pedido.id}`,
        text: `PDF do pedido #${pedido.id}`,
      };

      // Compartilha o arquivo PDF (não o link). Requer suporte a Web Share Level 2.
      if (navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        return;
      }

      // Fallback: baixa o arquivo quando o navegador não permite compartilhar File.
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      link.click();
      URL.revokeObjectURL(url);
      contextApi.showMessage(
        "Seu navegador não suporta compartilhar arquivos. O PDF foi baixado.",
        true,
        "info"
      );
    } catch (error: any) {
      // AbortError = usuário cancelou o share sheet; não é erro.
      if (error?.name === "AbortError") {
        return;
      }
      contextApi.showMessage(
        "Não foi possível compartilhar o PDF do pedido.",
        true,
        "error"
      );
    }
  };

  const handleDeleteItem = (pedido: Pedido) => {
    pedidoService
      .deleteItem(`/api/Pedidos/Deletar/${pedido.id}`)
      .then((response) => {
        if (response.status == 204) {
          contextApi.showMessage(
            "Pedido excluído com sucesso!",
            true,
            "success"
          );
          const filtroPesquisa: FiltroPesquisarPedido = {
            idPedido: 0,
            nomeCliente: '',
            dataInicial: dataInicial != null ? dataInicial.toISOString() : null,
            dataFinal: dataFinal != null ? dataFinal.toISOString() : null,
          };
          PesquisarPor(filtroPesquisarPedido);
        } else {
          contextApi.showMessage(
            "Erro ao excluir o Pedido, tente novamente!",
            true,
            "error"
          );
        }
      })
      .catch((error) => {
        contextApi.showMessage(error.response.data, true, "error");
      });
  };

  const openPDFInNewTab = async (pedido: Pedido) => {
    const blob = await pdf(
      <ImprimirPedido data={pedido} numeroPedido={`#000${pedido.id}`} />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const PesquisarPor = (filtroPesquisarPedido: FiltroPesquisarPedido) => {
    setLoading(true);

    const filtroPesquisa: FiltroPesquisarPedido = {
       idPedido: filtroPesquisarPedido.idPedido,
       nomeCliente: filtroPesquisarPedido.nomeCliente,
       dataInicial: dataInicial != null ? dataInicial.toISOString() : null,
       dataFinal: dataFinal != null ? dataFinal.toISOString() : null,
     };

    pedidoService
      .buscarPor(filtroPesquisa)
      .then((response) => {
        if (response.status === 200) {
          setPedidos(response.data); // Updates the pedidos state with the response data
          setFiltroPesquisarPedido(estadoInicialFiltroPesquisarPedido);
          setLoading(false);
        }
      })
      .catch((error) => {
        const errorMessage = error.response?.data || "An error occurred"; // Handle potential undefined error responses
        contextApi.showMessage(errorMessage, true, "error");
        setLoading(false);
      });
  };

  const handleEditPedido = (row: Pedido) => {
    navigate(`/app/gerarpedidos/${row.id}`);
  };

  const renderPedidoActions = (row: Pedido) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      <Button
        color="primary"
        size="small"
        onClick={() => handlePrint(row)}
        title="Imprimir PDF"
      >
        <PrintIcon />
      </Button>
      <Button
        color="primary"
        size="small"
        onClick={() => handleShare(row)}
        title="Compartilhar PDF"
      >
        <ShareIcon />
      </Button>
      <Button
        size="small"
        color="primary"
        onClick={() => handleEditPedido(row)}
        title="Editar pedido"
      >
        <EditIcon />
      </Button>
      <Button
        size="small"
        color="primary"
        onClick={() => handleDeleteItem(row)}
        title="Excluir pedido"
      >
        <DeleteIcon />
      </Button>
    </Box>
  );

  React.useEffect(() => {
    const filtroPesquisa: FiltroPesquisarPedido = {
      idPedido: 0,
      nomeCliente: '',
      dataInicial: dataInicial != null ? dataInicial.toISOString() : null,
      dataFinal: dataFinal != null ? dataFinal.toISOString() : null,
    };
    PesquisarPor(filtroPesquisarPedido);
  }, []); // Empty array ensures this runs only once when the component mounts

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 1, sm: 2 } }}>
        <Grid container spacing={2}>
          {/* Linha 1 - Cabeçalho e Descrição */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, bgcolor: '#f8f9fa' }}>
              <h4 style={{ margin: '0 0 0 0', color: colors.bluePrimary, textAlign: 'left' }}>
                🔍 Consultar Pedidos
              </h4>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Pesquisa e visualização de pedidos existentes
              </Typography>
            </Paper>
          </Grid>

          {/* Linha 2 - Filtros de Pesquisa */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>🔍 Filtros de Pesquisa</h4>
              
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <Grid container spacing={2} alignItems="center">
                  {/* Filtro ID Pedido */}
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      name="id"
                      label="ID Pedido"
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={filtroPesquisarPedido.idPedido}
                      onChange={(e) =>
                        handleInputPesquisarPedido("idPedido", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Filtro Nome Cliente */}
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      name="nome"
                      label="Cliente"
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={filtroPesquisarPedido.nomeCliente}
                      onChange={(e) =>
                        handleInputPesquisarPedido("nomeCliente", e.target.value)
                      }
                    />
                  </Grid>

                  {/* Filtro Data Inicial */}
                  <Grid item xs={12} sm={6} md={2}>
                      <DatePicker
                        label="Data Inicial"
                        value={dataInicial}
                        format="DD/MM/YYYY"
                        onChange={(newValue) =>
                          setDataInicial(newValue ?? dataInicial)
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true
                          }
                        }}
                      />
                  </Grid>

                  {/* Filtro Data Final */}
                  <Grid item xs={12} sm={6} md={2}>
                      <DatePicker
                        label="Data Final"
                        value={dataFinal}
                        format="DD/MM/YYYY"
                        onChange={(newValue) =>
                          setDataFinal(newValue ?? dataFinal)
                        }
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true
                          }
                        }}
                      />
                  </Grid>

                  {/* Botão Pesquisar */}
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{ height: '40px' }}
                      onClick={() => PesquisarPor(filtroPesquisarPedido)}
                    >
                      {loading ? (
                        <DefaultLoader
                          height="2.5em"
                          width="2.5em"
                          colors={["#1876D2", "#1876D2", "#1876D2", "#1876D2", "#1876D2"]}
                          textInfo="Consultando Pedidos"
                        />
                      ) : (
                        "Pesquisar"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </LocalizationProvider>
            </Paper>
          </Grid>

          {/* Linha 3 - Lista de Pedidos */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, mb: 1 }}>
              <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📋 Lista de Pedidos</h4>
              
              <Paper sx={{ p: { xs: 1, sm: 2 }, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
                {pedidos.length > 0 ? (
                  <>
                    {/* Mobile: cards */}
                    <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.5 }}>
                      {pedidos.map((row) => (
                        <Paper
                          key={row.id}
                          variant="outlined"
                          sx={{ p: 1.5 }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: 1 }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={700}>
                                Pedido #{row.id}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatDateToDDMMYYYY(row.dataCriacao)}
                              </Typography>
                            </Box>
                            <Typography variant="subtitle1" fontWeight={700} color="primary">
                              {formatToBRL(row.valorTotal)}
                            </Typography>
                          </Box>

                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Cliente:</strong> {row.cliente?.nome}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Telefone:</strong> {row.cliente?.telefone}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Pagamento:</strong>{" "}
                            {row.formaPagamento ||
                              row.pagamentosPedido?.map((p) => p.formaPagamento).join(", ")}
                          </Typography>

                          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                            Itens
                          </Typography>
                          {row.itensPedido?.map((item) => (
                            <Typography key={item.idProduto} variant="body2" color="text.secondary">
                              {item.quantidade}x {item.descricao} — {formatToBRL(item.preco)}
                            </Typography>
                          ))}

                          <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px solid #eee" }}>
                            {renderPedidoActions(row)}
                          </Box>
                        </Paper>
                      ))}
                    </Box>

                    {/* Desktop: tabela */}
                    <TableContainer sx={{ display: { xs: "none", md: "block" }, overflowX: "auto" }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Data</TableCell>
                            <TableCell>Valor</TableCell>
                            <TableCell>Cliente</TableCell>
                            <TableCell>Itens do Pedido</TableCell>
                            <TableCell>Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pedidos.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>{row.id}</TableCell>
                              <TableCell>
                                {formatDateToDDMMYYYY(row.dataCriacao)}
                              </TableCell>
                              <TableCell>{formatToBRL(row.valorTotal)}</TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  <strong>Nome:</strong> {row.cliente?.nome}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Documento:</strong> {row.cliente?.documento}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Telefone:</strong> {row.cliente?.telefone}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Email:</strong> {row.cliente?.email}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Endereço:</strong>{" "}
                                  {`${row.cliente?.cep}, ${row.cliente?.logradouro}, ${row.cliente?.numero}, ${row.cliente?.complemento}, ${row.cliente?.bairro}, ${row.cliente?.cidade} - ${row.cliente?.estado}`}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {row.itensPedido?.map((item) => (
                                  <div key={item.idProduto}>
                                    <Typography variant="body2">
                                      <strong>Produto:</strong> {item.descricao}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Quantidade:</strong> {item.quantidade}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Preço:</strong> {formatToBRL(item.preco)}
                                    </Typography>
                                  </div>
                                ))}
                                <Typography variant="body2">
                                  <strong>Forma de Pagamento:</strong>{" "}
                                  {row.formaPagamento || row.pagamentosPedido.map((p) => p.formaPagamento).join(", ")}
                                </Typography>
                                {row.quantidadeParcelas > 0 && (
                                  <Typography variant="body2">
                                    <strong>Quantidade Parcelas:</strong>{" "}
                                    {row.quantidadeParcelas}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {renderPedidoActions(row)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <TablePagination
                      rowsPerPageOptions={[10]}
                      component="div"
                      count={pedidos.length ?? 0}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      labelRowsPerPage="Linhas:"
                      sx={{
                        ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                          mb: 0,
                        },
                      }}
                    />
                  </>
                ) : (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Nenhum pedido encontrado para os filtros selecionados
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );  
}
